import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import AdminProfilePanel from '../../components/admin/AdminProfilePanel';
import AcademiaDashboardPanel from '../../components/admin/academia/AcademiaDashboardPanel';
import AcademiaInscriptionsPanel, {
  type InscriptionRow,
} from '../../components/admin/academia/AcademiaInscriptionsPanel';
import AcademiaApprenantsPanel from '../../components/admin/academia/AcademiaApprenantsPanel';
import AcademiaApprenantDetailPanel from '../../components/admin/academia/AcademiaApprenantDetailPanel';
import AcademiaFormationsPanel from '../../components/admin/academia/AcademiaFormationsPanel';
import AcademiaPlanningPanel from '../../components/admin/academia/AcademiaPlanningPanel';
import AcademiaChargeHorairePanel from '../../components/admin/academia/AcademiaChargeHorairePanel';
import AcademiaFormateursPanel from '../../components/admin/academia/AcademiaFormateursPanel';
import AcademiaAmbassadeursPanel from '../../components/admin/academia/AcademiaAmbassadeursPanel';
import AcademiaAmbassadeurDetailPanel from '../../components/admin/academia/AcademiaAmbassadeurDetailPanel';
import AcademiaAcademicPanel from '../../components/admin/academia/AcademiaAcademicPanel';
import AcademiaDocumentsPanel from '../../components/admin/academia/AcademiaDocumentsPanel';
import AcademiaTarifsPanel from '../../components/admin/academia/AcademiaTarifsPanel';
import AcademiaPaiementsPanel from '../../components/admin/academia/AcademiaPaiementsPanel';
import AcademiaCurrencyPanel from '../../components/admin/academia/AcademiaCurrencyPanel';
import AcademiaPublicSitePanel from '../../components/admin/academia/AcademiaPublicSitePanel';
import AcademiaPartenairesSponsorsPanel from '../../components/admin/academia/AcademiaPartenairesSponsorsPanel';
import AcademiaPartenaireSponsorDetailPanel from '../../components/admin/academia/AcademiaPartenaireSponsorDetailPanel';
import AcademiaApplicationSettingsPanel from '../../components/admin/academia/AcademiaApplicationSettingsPanel';
import AcademiaParrainagePanel from '../../components/admin/academia/AcademiaParrainagePanel';
import AcademiaWalletAdminPanel from '../../components/admin/academia/AcademiaWalletAdminPanel';
import AcademiaExcellenceAdminPanel from '../../components/admin/academia/AcademiaExcellenceAdminPanel';
import AcademiaLogistiquePanel from '../../components/admin/academia/AcademiaLogistiquePanel';
import AcademiaElearningPanel from '../../components/admin/academia/AcademiaElearningPanel';
import Module11CommunicationPanel from '../../components/admin/academia/Module11CommunicationPanel';
import Module12SecurityPanel from '../../components/admin/academia/Module12SecurityPanel';
import AdminCorbeillePanel from '../../components/admin/academia/AdminCorbeillePanel';
import AcademiaEquipePanel from '../../components/admin/academia/AcademiaEquipePanel';
import ApprenantActivityJournalPanel from '../../components/admin/academia/ApprenantActivityJournalPanel';
import ApprenantActivityDetailPanel from '../../components/admin/academia/ApprenantActivityDetailPanel';
import FormateurActivityJournalPanel from '../../components/admin/academia/FormateurActivityJournalPanel';
import FormateurActivityDetailPanel from '../../components/admin/academia/FormateurActivityDetailPanel';
import AmbassadeurActivityJournalPanel from '../../components/admin/academia/AmbassadeurActivityJournalPanel';
import AmbassadeurActivityDetailPanel from '../../components/admin/academia/AmbassadeurActivityDetailPanel';
import AdminAuditActionDetailPanel from '../../components/admin/academia/AdminAuditActionDetailPanel';
import PortalNotificationsPanel from '../../components/shared/PortalNotificationsPanel';
import PortalHelpPanel from '../../components/shared/PortalHelpPanel';
import { PORTAL_HELP_PATHS } from '../../config/portalHelpContent';
import {
  filterAdminNavigation,
  resolveAdminViewModule,
  resolveModerateurHomePath,
  userHasAdminModule,
} from '../../constants/adminModules';
import { ROLE_ADMIN } from '../../constants/roles';
import type { AdminBreadcrumbSegment } from '../../components/admin/AdminBreadcrumb';
import { ADMIN_NAV_SECTIONS } from '../../config/adminNavigation';
import type { AdminNavSection } from '../../types/admin';
import {
  useAcademiaAdminDashboard,
  useAcademiaAdminInscriptions,
  useAcademiaApprenants,
  useAcademiaCurrencySettings,
  useAcademiaFormations,
  useAcademiaTarifs,
} from '../../hooks/useAcademiaAdmin';

interface AdminAcademiaPageProps {
  user: {
    id?: string;
    role?: string;
    adminModules?: string[];
    telephone?: string;
    email?: string;
    profil?: {
      prenom?: string;
      nom?: string;
      photoUrl?: string | null;
    };
  };
  onLogout: () => void;
  onUserUpdated?: (user: AdminAcademiaPageProps['user']) => void;
}

const NAVIGATION: AdminNavSection[] = ADMIN_NAV_SECTIONS;

const BREADCRUMB_LABELS: Record<string, string> = {
  dashboard: 'Tableau de bord',
  inscriptions: 'Inscriptions',
  paiements: 'Paiements',
  apprenants: 'Apprenants',
  activitesApprenants: 'Activités apprenants',
  activiteApprenantDetail: 'Détail activité apprenant',
  activitesFormateurs: 'Activités formateurs',
  activiteFormateurDetail: 'Détail activité formateur',
  activitesAmbassadeurs: 'Activités ambassadeurs',
  activiteAmbassadeurDetail: 'Détail activité ambassadeur',
  formations: 'Formations',
  planning: 'Planning vacations',
  chargeHoraire: 'Charge horaire',
  formateurs: 'Gestion des formateurs',
  ambassadeurs: 'Ambassadeurs',
  ambassadeurDetail: 'Fiche ambassadeur',
  academique: 'Gestion académique',
  elearning: 'E-learning',
  excellence: 'Excellence & employabilité',
  documents: 'Validation dossiers',
  logistique: 'Logistique & patrimoine',
  communication: 'Communication & notifications',
  security: 'Sécurité & traçabilité',
  corbeille: 'Corbeille',
  equipe: 'Équipe & accès',
  securityActionDetail: 'Détail de l\'action',
  notifications: 'Mes notifications',
  parrainage: 'Parrainage',
  wallet: 'Portefeuilles Academia',
  tarifs: 'Tarification des frais',
  site: 'Site & QR',
  partenairesSponsors: 'Partenaires & sponsors',
  parametresApp: 'Paramètres',
  settings: 'Devise & taux',
  aide: 'Centre d’aide',
  profile: 'Mon profil',
  apprenantDetail: 'Fiche apprenant',
};

export default function AdminAcademiaPage({ user, onLogout, onUserUpdated }: AdminAcademiaPageProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const auditDetailMatch = location.pathname.match(/^\/admin\/security\/actions\/([^/]+)$/);
  const auditDetailId = auditDetailMatch?.[1] ?? null;
  const auditActionLabel =
    (location.state as { actionLabel?: string } | null)?.actionLabel ?? 'Détail de l\'action';

  const activityDetailMatch = location.pathname.match(/^\/admin\/activites-apprenants\/([^/]+)$/);
  const activityDetailId = activityDetailMatch?.[1] ?? null;
  const activityDetailLabel =
    (location.state as { activityLabel?: string } | null)?.activityLabel ?? 'Détail activité apprenant';

  const formateurActivityDetailMatch = location.pathname.match(/^\/admin\/activites-formateurs\/([^/]+)$/);
  const formateurActivityDetailId = formateurActivityDetailMatch?.[1] ?? null;
  const formateurActivityDetailLabel =
    (location.state as { activityLabel?: string } | null)?.activityLabel ?? 'Détail activité formateur';

  const ambassadeurActivityDetailMatch = location.pathname.match(/^\/admin\/activites-ambassadeurs\/([^/]+)$/);
  const ambassadeurActivityDetailId = ambassadeurActivityDetailMatch?.[1] ?? null;
  const ambassadeurActivityDetailLabel =
    (location.state as { activityLabel?: string } | null)?.activityLabel ?? 'Détail activité ambassadeur';

  const ambassadeurDetailMatch = location.pathname.match(/^\/admin\/ambassadeurs\/([^/]+)$/);
  const ambassadeurDetailId = ambassadeurDetailMatch?.[1] ?? null;
  const ambassadeurDetailLabel =
    (location.state as { ambassadeurLabel?: string } | null)?.ambassadeurLabel ?? 'Fiche ambassadeur';

  const apprenantDetailMatch = location.pathname.match(/^\/admin\/apprenants\/([^/]+)$/);
  const apprenantDetailId = apprenantDetailMatch?.[1] ?? null;
  const apprenantDetailLabel =
    (location.state as { apprenantLabel?: string } | null)?.apprenantLabel ?? 'Fiche apprenant';

  const dashboard = useAcademiaAdminDashboard();
  const inscriptions = useAcademiaAdminInscriptions();
  const apprenants = useAcademiaApprenants();
  const formations = useAcademiaFormations();
  const tarifs = useAcademiaTarifs();
  const currency = useAcademiaCurrencySettings();

  const fullName = [user.profil?.prenom, user.profil?.nom].filter(Boolean).join(' ');
  const normalizedSearch = searchQuery.trim().toLowerCase();

  const filterInscriptionRows = useCallback(
    (rows: InscriptionRow[]): InscriptionRow[] => {
      if (!normalizedSearch) return rows;
      return rows.filter((row) => {
        const apprenant = row.apprenant as { telephone?: string; profil?: { prenom?: string; nom?: string } } | undefined;
        const filiere = row.filiere as { libelle?: string; code?: string } | undefined;
        const cohorte = row.cohorte as { code?: string } | undefined;
        const values = [
          row.statut,
          row.matriculeSnapshot,
          apprenant?.telephone,
          apprenant?.profil?.prenom,
          apprenant?.profil?.nom,
          filiere?.libelle,
          filiere?.code,
          cohorte?.code,
        ];
        return values.filter(Boolean).some((v) => String(v).toLowerCase().includes(normalizedSearch));
      });
    },
    [normalizedSearch],
  );

  const filteredInscriptions = useMemo(
    () => ({
      enAttente: filterInscriptionRows(inscriptions.enAttente as InscriptionRow[]),
      actives: filterInscriptionRows(inscriptions.actives as InscriptionRow[]),
    }),
    [filterInscriptionRows, inscriptions.enAttente, inscriptions.actives],
  );

  const filteredApprenants = useMemo(() => {
    if (!normalizedSearch) return apprenants.items;
    return apprenants.items.filter((row: Record<string, unknown>) => {
      const profil = row.profil as { prenom?: string; nom?: string } | undefined;
      const pa = row.profilApprenant as { matricule?: string } | undefined;
      const values = [row.telephone, row.email, profil?.prenom, profil?.nom, pa?.matricule];
      return values.filter(Boolean).some((v) => String(v).toLowerCase().includes(normalizedSearch));
    });
  }, [apprenants.items, normalizedSearch]);

  const viewKey = useMemo(() => {
    const path = location.pathname;
    if (path === '/admin' || path === '/admin/dashboard') return 'dashboard';
    if (path.startsWith('/admin/inscriptions')) return 'inscriptions';
    if (apprenantDetailId) return 'apprenantDetail';
    if (path.startsWith('/admin/apprenants')) return 'apprenants';
    if (activityDetailId) return 'activiteApprenantDetail';
    if (path.startsWith('/admin/activites-apprenants')) return 'activitesApprenants';
    if (formateurActivityDetailId) return 'activiteFormateurDetail';
    if (path.startsWith('/admin/activites-formateurs')) return 'activitesFormateurs';
    if (ambassadeurActivityDetailId) return 'activiteAmbassadeurDetail';
    if (path.startsWith('/admin/activites-ambassadeurs')) return 'activitesAmbassadeurs';
    if (path.startsWith('/admin/formations')) return 'formations';
    if (path.startsWith('/admin/planning')) return 'planning';
    if (path.startsWith('/admin/charge-horaire')) return 'chargeHoraire';
    if (path.startsWith('/admin/formateurs')) return 'formateurs';
    if (ambassadeurDetailId) return 'ambassadeurDetail';
    if (path.startsWith('/admin/ambassadeurs')) return 'ambassadeurs';
    if (path.startsWith('/admin/academique')) return 'academique';
    if (path.startsWith('/admin/elearning')) return 'elearning';
    if (path.startsWith('/admin/documents')) return 'documents';
    if (path.startsWith('/admin/logistique')) return 'logistique';
    if (path.startsWith('/admin/communication')) return 'communication';
    if (auditDetailId) return 'securityActionDetail';
    if (path.startsWith('/admin/security')) return 'security';
    if (path.startsWith('/admin/corbeille')) return 'corbeille';
    if (path.startsWith('/admin/notifications')) return 'notifications';
    if (path.startsWith('/admin/paiements')) return 'paiements';
    if (path.startsWith('/admin/parrainage')) return 'parrainage';
    if (path.startsWith('/admin/wallet')) return 'wallet';
    if (path.startsWith('/admin/excellence')) return 'excellence';
    if (path.startsWith('/admin/tarifs')) return 'tarifs';
    if (path.startsWith('/admin/parametres-app')) return 'parametresApp';
    if (path.startsWith('/admin/partenaires-sponsors')) return 'partenairesSponsors';
    if (path.startsWith('/admin/site')) return 'site';
    if (path.startsWith('/admin/settings')) return 'settings';
    if (path.startsWith('/admin/aide')) return 'aide';
    if (path.startsWith('/admin/equipe')) return 'equipe';
    if (path.startsWith('/admin/profile')) return 'profile';
    return 'dashboard';
  }, [location.pathname, apprenantDetailId, auditDetailId, activityDetailId, formateurActivityDetailId, ambassadeurActivityDetailId, ambassadeurDetailId]);

  const breadcrumb: AdminBreadcrumbSegment[] = useMemo(() => {
    const base: AdminBreadcrumbSegment[] = [{ label: 'Administration' }];
    if (auditDetailId) {
      return [
        ...base,
        { label: 'Sécurité & traçabilité', onActivate: () => navigate('/admin/security') },
        { label: auditActionLabel },
      ];
    }
    if (activityDetailId) {
      return [
        ...base,
        { label: 'Activités apprenants', onActivate: () => navigate('/admin/activites-apprenants') },
        { label: activityDetailLabel },
      ];
    }
    if (formateurActivityDetailId) {
      return [
        ...base,
        { label: 'Activités formateurs', onActivate: () => navigate('/admin/activites-formateurs') },
        { label: formateurActivityDetailLabel },
      ];
    }
    if (ambassadeurActivityDetailId) {
      return [
        ...base,
        { label: 'Activités ambassadeurs', onActivate: () => navigate('/admin/activites-ambassadeurs') },
        { label: ambassadeurActivityDetailLabel },
      ];
    }
    if (apprenantDetailId) {
      return [
        ...base,
        { label: 'Apprenants', onActivate: () => navigate('/admin/apprenants') },
        { label: apprenantDetailLabel },
      ];
    }
    if (ambassadeurDetailId) {
      return [
        ...base,
        { label: 'Ambassadeurs', onActivate: () => navigate('/admin/ambassadeurs') },
        { label: ambassadeurDetailLabel },
      ];
    }
    return [...base, { label: BREADCRUMB_LABELS[viewKey] ?? 'Tableau de bord' }];
  }, [apprenantDetailId, apprenantDetailLabel, ambassadeurDetailId, ambassadeurDetailLabel, auditDetailId, auditActionLabel, activityDetailId, activityDetailLabel, formateurActivityDetailId, formateurActivityDetailLabel, ambassadeurActivityDetailId, ambassadeurActivityDetailLabel, navigate, viewKey]);

  const navigation = useMemo(() => {
    let sections: AdminNavSection[] =
      user.role === ROLE_ADMIN
        ? NAVIGATION
        : NAVIGATION.map((section) => ({
            ...section,
            items: section.items.filter(
              (item) =>
                item.id !== 'equipe' &&
                item.id !== 'security' &&
                item.id !== 'corbeille',
            ),
          })).filter((section) => section.items.length > 0);
    return filterAdminNavigation(sections, user);
  }, [user]);

  useEffect(() => {
    if (user.role !== 'MODERATEUR') return;
    const requiredModule = resolveAdminViewModule(viewKey);
    if (!userHasAdminModule(user, requiredModule)) {
      navigate(resolveModerateurHomePath(user), { replace: true });
    }
  }, [navigate, user, viewKey]);

  const portalSubtitle =
    user.role === 'MODERATEUR' ? 'Modérateur' : user.role === ROLE_ADMIN ? 'Administrateur' : 'Centre de formation';

  return (
    <AdminLayout
      navigation={navigation}
      brandAlt="Logo JCONSULT MY Academia"
      onLogout={onLogout}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      breadcrumb={breadcrumb}
      helpPath={PORTAL_HELP_PATHS.admin}
      user={{
        name: fullName || user.telephone || 'Administrateur',
        subtitle: portalSubtitle,
        photoUrl: user.profil?.photoUrl,
        prenom: user.profil?.prenom,
        nom: user.profil?.nom,
      }}
    >
      <Routes>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route
          path="dashboard"
          element={
            <AcademiaDashboardPanel
              user={user}
              userName={fullName || user.email || 'Administrateur'}
              data={dashboard.data}
              isLoading={dashboard.isLoading}
              isError={dashboard.isError}
              onRefresh={dashboard.refetch}
            />
          }
        />
        <Route path="paiements" element={<AcademiaPaiementsPanel />} />
        <Route
          path="inscriptions"
          element={
            <AcademiaInscriptionsPanel
              user={user}
              enAttente={filteredInscriptions.enAttente}
              actives={filteredInscriptions.actives}
              isLoading={inscriptions.isLoading}
              isError={inscriptions.isError}
              onRefresh={inscriptions.refetch}
            />
          }
        />
        <Route path="apprenants">
          <Route
            index
            element={
              <AcademiaApprenantsPanel
                user={user}
                items={filteredApprenants}
                isLoading={apprenants.isLoading}
                isError={apprenants.isError}
                onRefresh={apprenants.refetch}
              />
            }
          />
          <Route path=":apprenantId" element={<AcademiaApprenantDetailPanel />} />
        </Route>
        <Route path="activites-apprenants">
          <Route index element={<ApprenantActivityJournalPanel />} />
          <Route path=":activityId" element={<ApprenantActivityDetailPanel />} />
        </Route>
        <Route path="activites-formateurs">
          <Route index element={<FormateurActivityJournalPanel />} />
          <Route path=":activityId" element={<FormateurActivityDetailPanel />} />
        </Route>
        <Route path="activites-ambassadeurs">
          <Route index element={<AmbassadeurActivityJournalPanel />} />
          <Route path=":activityId" element={<AmbassadeurActivityDetailPanel />} />
        </Route>
        <Route path="planning" element={<AcademiaPlanningPanel user={user} />} />
        <Route path="charge-horaire" element={<AcademiaChargeHorairePanel />} />
        <Route path="formateurs" element={<AcademiaFormateursPanel user={user} />} />
        <Route path="ambassadeurs">
          <Route index element={<AcademiaAmbassadeursPanel user={user} />} />
          <Route path=":ambassadeurId" element={<AcademiaAmbassadeurDetailPanel />} />
        </Route>
        <Route path="academique" element={<AcademiaAcademicPanel user={user} />} />
        <Route path="elearning" element={<AcademiaElearningPanel user={user} />} />
        <Route path="documents" element={<AcademiaDocumentsPanel />} />
        <Route path="logistique" element={<AcademiaLogistiquePanel />} />
        <Route path="communication" element={<Module11CommunicationPanel user={user} />} />
        <Route path="security">
          <Route index element={<Module12SecurityPanel user={user} />} />
          <Route path="actions/:auditId" element={<AdminAuditActionDetailPanel />} />
        </Route>
        <Route path="corbeille" element={<AdminCorbeillePanel user={user} />} />
        <Route path="equipe" element={<AcademiaEquipePanel />} />
        <Route path="notifications" element={<PortalNotificationsPanel portal="admin" />} />
        <Route
          path="formations"
          element={
            <AcademiaFormationsPanel
              user={user}
              filieres={formations.filieres}
              cohortes={formations.cohortes}
              selectedFiliereId={formations.selectedFiliereId}
              onFiliereChange={formations.setSelectedFiliereId}
              isLoading={formations.isLoading}
              isError={formations.isError}
              onRefresh={formations.refetch}
              reloadCohortes={formations.reloadCohortes}
            />
          }
        />
        <Route path="parrainage" element={<AcademiaParrainagePanel />} />
        <Route path="wallet" element={<AcademiaWalletAdminPanel />} />
        <Route path="excellence" element={<AcademiaExcellenceAdminPanel user={user} />} />
        <Route
          path="tarifs"
          element={
            <AcademiaTarifsPanel
              items={tarifs.items}
              formateur={tarifs.formateur}
              parrainage={tarifs.parrainage}
              isLoading={tarifs.isLoading}
              isError={tarifs.isError}
              isSaving={tarifs.isSaving}
              toastMessage={tarifs.toastMessage}
              toastVariant={tarifs.toastVariant === 'error' ? 'error' : 'success'}
              onDismissToast={tarifs.clearToast}
              onRefresh={tarifs.refetch}
              onSaveFormation={tarifs.saveFormationTarif}
              onSaveFormateur={tarifs.saveFormateurTarif}
              onSaveParrainageBonus={tarifs.saveParrainageBonus}
              onSaveParrainageRegle={tarifs.saveParrainageRegle}
            />
          }
        />
        <Route path="parametres-app" element={<AcademiaApplicationSettingsPanel user={user} />} />
        <Route path="partenaires-sponsors">
          <Route index element={<AcademiaPartenairesSponsorsPanel />} />
          <Route path=":partenaireId" element={<AcademiaPartenaireSponsorDetailPanel />} />
        </Route>
        <Route path="site" element={<AcademiaPublicSitePanel />} />
        <Route
          path="settings"
          element={
            <AcademiaCurrencyPanel
              settings={currency.settings}
              isLoading={currency.isLoading}
              isError={currency.isError}
              isSaving={currency.isSaving}
              toastMessage={currency.toastMessage}
              toastVariant={currency.toastVariant}
              onDismissToast={currency.clearToast}
              onRefresh={currency.refetch}
              onSaveCurrency={currency.saveCurrency}
            />
          }
        />
        <Route path="profile" element={<AdminProfilePanel user={user} onUserUpdated={onUserUpdated} />} />
        <Route path="aide" element={<PortalHelpPanel portal="admin" />} />
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Routes>
    </AdminLayout>
  );
}
